import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { GenerationAsset, VideoSettings } from '../state/types';
import { useGenerationModels } from '../../../features/generation-models/hooks';

/**
 * 動画モデルの能力 (参考画像枚数上限 / video-to-video 対応) を DB metadata から解決し、
 * モデル切替に伴う確認ダイアログの state を持つフック。
 *
 * GenerationWorkspace から切り出したもので、ロジックは移設のみ (挙動不変)。
 * ScenarioGenerator 側にも似た判定があるが、統合は別項目 (今回は本ワークスペース分のみ)。
 */
export function useVideoModelCapabilities({
  videoSettings,
  setVideoSettings,
  selectedAssets,
  setMaxReferenceVideos,
  setMaxReferenceAudios,
  removeAsset,
}: {
  videoSettings: VideoSettings;
  setVideoSettings: Dispatch<SetStateAction<VideoSettings>>;
  selectedAssets: GenerationAsset[];
  setMaxReferenceVideos: (count: number) => void;
  setMaxReferenceAudios: (count: number) => void;
  removeAsset: (id: string, opts?: { silent?: boolean }) => void;
}) {
  // 動画モデル別の参考画像枚数上限を DB metadata から解決
  // (Veo 3.1 = 3, Seedance 2.0 = 9 等。マイグレーション 20260511_add_max_reference_images_metadata.sql)
  const { data: videoModels = [] } = useGenerationModels('video');
  const activeVideoModel = useMemo(
    () => videoModels.find((m) => m.model_code === videoSettings.videoModel),
    [videoModels, videoSettings.videoModel],
  );
  // ⚠️ fromMeta > 0 の条件を付けると「metadata に明示された 0」(= 参照画像非対応。
  // 例: veo-3.1-lite は r2v 非対応のため max_reference_images=0 を明示的に持つ) が
  // else 側の既定値 3 に化けてしまう (0 は falsy だが「未設定」ではなく「明示的な 0」)。
  // metadata キーが無い (undefined/null) 場合のみ既定値にフォールバックする ('??' 相当)。
  const referenceImageLimit = useMemo(() => {
    const fromMeta = (activeVideoModel?.metadata as { max_reference_images?: number } | null | undefined)?.max_reference_images;
    return typeof fromMeta === 'number' ? fromMeta : 3;
  }, [activeVideoModel]);

  // 動画モデル別の参照動画 (video-to-video) 上限を DB metadata から解決し、ストアへ反映。
  // 0 = 動画入力非対応 (Veo)。これを単一の情報源として「動画素材を参照に選べるか」を判定する。
  // (マイグレーション 20260625_add_max_reference_videos_metadata.sql)
  // 上と同じ理由で fromMeta > 0 は使わない (明示的な 0 と未設定を区別する)。
  const referenceVideoLimit = useMemo(() => {
    const fromMeta = (activeVideoModel?.metadata as { max_reference_videos?: number } | null | undefined)?.max_reference_videos;
    return typeof fromMeta === 'number' ? fromMeta : 0;
  }, [activeVideoModel]);
  useEffect(() => {
    setMaxReferenceVideos(referenceVideoLimit);
  }, [referenceVideoLimit, setMaxReferenceVideos]);

  // 参照音声の上限 (マイグレーション 20260810_02_add_max_reference_audios_metadata.sql)。
  // 0 = 音声入力非対応 (Veo / Gemini Omni)。max_reference_videos と同じ規約で
  // 「明示的な 0」と「未設定」を区別しつつ、未設定は 0 (非対応) にフォールバックする。
  const referenceAudioLimit = useMemo(() => {
    const fromMeta = (activeVideoModel?.metadata as { max_reference_audios?: number } | null | undefined)?.max_reference_audios;
    return typeof fromMeta === 'number' ? fromMeta : 0;
  }, [activeVideoModel]);
  useEffect(() => {
    setMaxReferenceAudios(referenceAudioLimit);
  }, [referenceAudioLimit, setMaxReferenceAudios]);

  // 参照動画をサポートする直近のモデルを記憶 (非対応モデルへ切替時の「元に戻す」用)。
  const lastSupportingVideoModelRef = useRef<string>(videoSettings.videoModel);
  useEffect(() => {
    if (referenceVideoLimit > 0) {
      lastSupportingVideoModelRef.current = videoSettings.videoModel;
    }
  }, [referenceVideoLimit, videoSettings.videoModel]);

  // 動画入力非対応モデル (limit=0) へ切り替えたとき、選択済みの参照動画があれば
  // 黙って消さず確認モーダルを出す。残したままだと「フロントは V2V 見積り・backend は
  // 参照動画無視で通常レート」のズレが起きるため、外すか元モデルへ戻すかを選ばせる。
  // モデル一覧ロード前 (videoModels 未取得) は activeVideoModel が解決できず limit=0 に
  // なるため、復元直後の参照動画を誤検知しないよう「モデル解決済み」を条件にする。
  const [v2vSwitchConfirm, setV2vSwitchConfirm] = useState<{ previousModel: string; videoAssetIds: string[] } | null>(null);
  useEffect(() => {
    if (videoModels.length === 0 || !activeVideoModel) return;
    if (referenceVideoLimit > 0) return;
    if (v2vSwitchConfirm) return; // すでに確認中
    const staleVideoIds = selectedAssets
      .filter((a) => a.usage === 'reference_video')
      .map((a) => a.id);
    if (staleVideoIds.length === 0) return;
    setV2vSwitchConfirm({ previousModel: lastSupportingVideoModelRef.current, videoAssetIds: staleVideoIds });
  }, [videoModels.length, activeVideoModel, referenceVideoLimit, selectedAssets, v2vSwitchConfirm]);

  // Gemini Omni への切替推奨モーダル (recommendOmniConfirm) は 2026-08-10 廃止。
  // Seedance 2.5 / MiniMax H3 も動画編集に対応し、Omni へ誘導する理由が無くなったため。
  // v2v 非対応モデル → 対応モデルへの自動切替は assetsContext.resolveVideoEditModel が担う。

  const confirmV2vSwitch = () => {
    v2vSwitchConfirm?.videoAssetIds.forEach((id) => removeAsset(id));
    setV2vSwitchConfirm(null);
  };

  const cancelV2vSwitch = () => {
    const prev = v2vSwitchConfirm?.previousModel;
    if (prev) {
      setVideoSettings((s) => ({ ...s, videoModel: prev as typeof s.videoModel }));
    }
    setV2vSwitchConfirm(null);
  };

  return {
    referenceImageLimit,
    referenceVideoLimit,
    referenceAudioLimit,
    v2vSwitchConfirm,
    confirmV2vSwitch,
    cancelV2vSwitch,
  };
}
